import React, { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Images, Plus, Trash2, Bell, Loader2, X, Upload as UploadIcon,
} from "lucide-react";
import { toast } from "sonner";
import { ZuordnungPicker, ZuordnungLabel, istNeu } from "@/components/portal/ZuordnungPicker";

export const AdminGalerienTab = () => {
  const [gallerien, setGallerien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [aktiv, setAktiv] = useState(null); // aktive Galerie für Detail

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/galleries?include_inaktiv=true");
      setGallerien(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setNewOpen(true)} data-testid="galerie-neu">
          <Plus className="h-4 w-4 mr-2" /> Neue Galerie
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Images className="h-5 w-5 text-primary" /> Galerien ({gallerien.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : gallerien.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Keine Galerien angelegt.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {gallerien.map((g) => (
                <button key={g.id}
                  onClick={() => setAktiv(g)}
                  data-testid={`galerie-${g.id}`}
                  className="text-left rounded-lg border bg-card hover:bg-secondary/40 p-3 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{g.titel}</div>
                    {istNeu(g.erstellt_am, g.neu_badge_tage) && (
                      <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">Neu</Badge>
                    )}
                  </div>
                  {g.beschreibung && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{g.beschreibung}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <ZuordnungLabel typ={g.zuordnung_typ} ids={g.zuordnung_ids} />
                    <span className="text-muted-foreground">{g.foto_count || 0} Fotos</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {newOpen && (
        <NewGalerieDialog onClose={() => setNewOpen(false)}
          onDone={() => { setNewOpen(false); laden(); }} />
      )}
      {aktiv && (
        <GalerieDetailDialog galerie={aktiv} onClose={() => setAktiv(null)}
          onChanged={() => { setAktiv(null); laden(); }} />
      )}
    </div>
  );
};

const NewGalerieDialog = ({ onClose, onDone }) => {
  const [titel, setTitel] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [zuord, setZuord] = useState({ zuordnung_typ: "alle", zuordnung_ids: [] });
  const [gueltigAb, setGueltigAb] = useState("");
  const [gueltigBis, setGueltigBis] = useState("");
  const [neuBadgeTage, setNeuBadgeTage] = useState(7);
  const [benachrichtigen, setBenachrichtigen] = useState(false);
  const [running, setRunning] = useState(false);

  const speichern = async () => {
    if (!titel.trim()) { toast.error("Bitte Titel eingeben."); return; }
    if (zuord.zuordnung_typ !== "alle" && zuord.zuordnung_ids.length === 0) {
      toast.error("Bitte Zielgruppe konkretisieren."); return;
    }
    setRunning(true);
    try {
      await api.post("/galleries", {
        titel, beschreibung,
        zuordnung_typ: zuord.zuordnung_typ,
        zuordnung_ids: zuord.zuordnung_ids,
        gueltig_ab: gueltigAb || null,
        gueltig_bis: gueltigBis || null,
        neu_badge_tage: neuBadgeTage,
        benachrichtigen,
      });
      toast.success("Galerie angelegt – nun Fotos hochladen.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Neue Galerie</DialogTitle>
          <DialogDescription>
            Nach dem Anlegen können Sie Fotos hinzufügen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Titel</Label>
            <Input value={titel} onChange={(e) => setTitel(e.target.value)}
              placeholder="z. B. Lieferantenfest 2026" data-testid="gal-titel" />
          </div>
          <div>
            <Label className="text-sm">Beschreibung</Label>
            <Textarea rows={2} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
          </div>
          <ZuordnungPicker value={zuord} onChange={setZuord} testIdPrefix="gal-zuord" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Gültig ab</Label>
              <Input type="date" value={gueltigAb} onChange={(e) => setGueltigAb(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Gültig bis</Label>
              <Input type="date" value={gueltigBis} onChange={(e) => setGueltigBis(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm">Neu-Badge (Tage)</Label>
              <Input type="number" min={0} max={30} value={neuBadgeTage}
                onChange={(e) => setNeuBadgeTage(Number(e.target.value))} />
            </div>
          </div>
          <label className="flex items-center gap-2 rounded-md border p-2 bg-secondary/40 cursor-pointer">
            <Switch checked={benachrichtigen} onCheckedChange={setBenachrichtigen} />
            <div className="flex-1">
              <p className="text-sm flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Lieferanten benachrichtigen</p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={running} data-testid="gal-speichern">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GalerieDetailDialog = ({ galerie, onClose, onChanged }) => {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/galleries/${galerie.id}`);
      setFotos(data.fotos || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [galerie.id]);
  useEffect(() => { laden(); }, [laden]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/galleries/${galerie.id}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`${files.length} Foto(s) hochgeladen.`);
      laden();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUploading(false); }
  };

  const loeschenFoto = async (id) => {
    try {
      await api.delete(`/galleries/${galerie.id}/photos/${id}`);
      laden();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const galerieLoeschen = async () => {
    if (!window.confirm("Galerie mit allen Fotos endgültig löschen?")) return;
    try {
      await api.delete(`/galleries/${galerie.id}`);
      toast.success("Galerie gelöscht.");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Images className="h-5 w-5 text-primary" /> {galerie.titel}
          </DialogTitle>
          {galerie.beschreibung && (
            <DialogDescription>{galerie.beschreibung}</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ZuordnungLabel typ={galerie.zuordnung_typ} ids={galerie.zuordnung_ids} />
              <span>· {fotos.length} Foto(s)</span>
              {(galerie.gueltig_ab || galerie.gueltig_bis) && (
                <span>· {galerie.gueltig_ab ? fmtDate(galerie.gueltig_ab) : "sofort"} – {galerie.gueltig_bis ? fmtDate(galerie.gueltig_bis) : "unbegrenzt"}</span>
              )}
            </div>
            <div className="flex gap-2">
              <label className="inline-flex">
                <input type="file" hidden multiple accept="image/*"
                  onChange={(e) => upload(e.target.files)} data-testid="gal-fotos-upload" />
                <Button asChild size="sm" disabled={uploading}>
                  <span>{uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadIcon className="h-4 w-4 mr-2" />} Fotos hinzufügen</span>
                </Button>
              </label>
              <Button size="sm" variant="destructive" onClick={galerieLoeschen}
                data-testid="gal-loeschen">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : fotos.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              Noch keine Fotos in dieser Galerie.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {fotos.map((p) => (
                <div key={p.id} className="relative group rounded-md overflow-hidden border">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos/${p.id}/thumb`}
                    alt={p.dateiname}
                    className="w-full aspect-square object-cover"
                    onError={(e) => { e.target.style.opacity = 0.3; }} />
                  <Button size="icon" variant="destructive"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition"
                    onClick={() => loeschenFoto(p.id)}
                    data-testid={`foto-delete-${p.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
